// src/components/developer/FeedbackReader.js
import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  Box,
  Typography,
  TextField,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip,
  IconButton,
  Stack,
  Button,
  InputAdornment,
} from '@mui/material';
import TabView from '../shared/TabView';
import SplitView from '../shared/SplitView';
import axios from 'axios';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import EventNoteIcon from '@mui/icons-material/EventNote';
import InfoIcon from '@mui/icons-material/Info';
import SortIcon from '@mui/icons-material/Sort';
import DownloadIcon from '@mui/icons-material/Download';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import ArticleIcon from '@mui/icons-material/Article';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import Cookies from 'js-cookie';
import { SettingsContext } from '../SettingsContext';
import { STATUS_BAR_HEIGHT } from '../StatusBar';

// Helper function to get file extension from content type
const getExtensionFromContentType = (type) => {
  const mimeToExt = {
    'application/pdf': 'pdf',
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/bmp': 'bmp',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'text/plain': 'txt',
    'text/csv': 'csv',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx'
  };
  return mimeToExt[type] || 'bin';
};

// Hjelpefunksjon for å bestemme filtype-ikon
const getFileIcon = (filename, contentType) => {
  try {
    // Try to get extension from filename first
    let ext = filename ? filename.split('.').pop().toLowerCase() : null;
    
    // If filename is not available or has no extension, try to get from content type
    if (!ext && contentType) {
      ext = getExtensionFromContentType(contentType);
    }

    // If we still don't have an extension, return default icon
    if (!ext) return <InsertDriveFileIcon />;

    switch (ext) {
      case 'pdf':
        return <PictureAsPdfIcon />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
        return <ImageIcon />;
      case 'doc':
      case 'docx':
        return <DescriptionIcon />;
      case 'txt':
      case 'log':
      case 'csv':
        return <ArticleIcon />;
      default:
        return <InsertDriveFileIcon />;
    }
  } catch (error) {
    console.warn('Error getting file icon:', error);
    return <InsertDriveFileIcon />;
  }
};

// Hjelpefunksjon for å formatere filtype
const getFileType = (filename, contentType) => {
  try {
    // Try to get extension from filename first
    let ext = filename ? filename.split('.').pop().toLowerCase() : null;
    
    // If filename is not available or has no extension, try to get from content type
    if (!ext && contentType) {
      ext = getExtensionFromContentType(contentType);
    }

    // If we still don't have an extension, return Unknown
    if (!ext) return 'Unknown File';

    switch (ext) {
      case 'pdf':
        return 'PDF Document';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
        return 'Image';
      case 'doc':
      case 'docx':
        return 'Word Document';
      case 'txt':
      case 'log':
        return 'Text File';
      case 'csv':
        return 'CSV File';
      case 'xls':
      case 'xlsx':
        return 'Excel Document';
      default:
        return ext.toUpperCase() + ' File';
    }
  } catch (error) {
    console.warn('Error getting file type:', error);
    return 'Unknown File';
  }
};

// Hjelpefunksjon for datoformatering
const formatDateTime = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  
  const dato = d.getDate().toString().padStart(2, '0');
  const måned = (d.getMonth() + 1).toString().padStart(2, '0');
  const år = d.getFullYear();
  const time = d.getHours().toString().padStart(2, '0');
  const minutt = d.getMinutes().toString().padStart(2, '0');
  
  return `${dato}.${måned}.${år} ${time}:${minutt}`;
};

function getAuthHeaders() {
  const token = Cookies.get('token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

const columns = [
  { field: 'id', headerName: 'ID', width: 70 },
  { field: 'timestamp', headerName: 'Date', width: 160, valueGetter: (params) => new Date(params.value).toLocaleString() },
  { field: 'business_area', headerName: 'Business Area', width: 180 },
  { field: 'contact_person', headerName: 'Contact', width: 140 },
  { field: 'email', headerName: 'Email', width: 180 },
  { field: 'workflow_description', headerName: 'Workflow Description', width: 220 },
  { field: 'priority', headerName: 'Priority', width: 90 },
  { field: 'boredom_level', headerName: 'Tedium', width: 90 },
];

function getFileSize(size) {
  if (size === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(size) / Math.log(k));
  return parseFloat((size / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

function FeedbackDetail({ submission, handleTagClick }) {
  if (!submission) return null;
  return (
    <Paper sx={{ p: 2, bgcolor: '#232323', color: '#fff', border: '1px solid #444' }}>
      {/* Header with Location and Description */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ color: '#4a9bfc', fontWeight: 500 }}>
            Submission Details
          </Typography>
          <Stack direction="row" spacing={1}>
            <Chip size="small" icon={<EventNoteIcon />} label={formatDateTime(submission.timestamp)} />
            <Chip 
              size="small" 
              icon={<PersonIcon />} 
              label={submission.contact_person || 'N/A'} 
              onClick={() => handleTagClick(`contact-${submission.contact_person}`)}
              sx={{ cursor: 'pointer' }}
            />
            <Chip 
              size="small" 
              icon={<EmailIcon />} 
              label={submission.email || 'N/A'} 
              variant="outlined"
              onClick={() => handleTagClick(`email-${submission.email}`)}
              sx={{ cursor: 'pointer' }}
            />
            <Chip size="small" icon={<InfoIcon />} label={`#${submission.id}`} variant="outlined" />
          </Stack>
        </Box>
        <Box sx={{ ml: 2 }}>
          {/* Location Section */}
          <Typography variant="subtitle2" color="#aaa">Location / Business Area</Typography>
          <Typography sx={{ mb: 2 }}>
            {submission.business_area || 'No Business Area'}
          </Typography>

          {/* Description Section */}
          <Typography variant="subtitle1" sx={{ color: '#4a9bfc', mb: 1, mt: 2, fontWeight: 500 }}>
            Request
          </Typography>
          <Typography sx={{ mb: 2, ml: 2 }}>
            {submission.workflow_description || 'No description'}
          </Typography>
        </Box>
      </Box>

      {/* Main Content Grid */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(2, 1fr)', 
        gap: 3,
        mb: 3 
      }}>
        {/* Left Column */}
        <Box>
          {/* Core Business Information */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ color: '#4a9bfc', mb: 1, fontWeight: 500 }}>
              Process Overview
            </Typography>
            <Box sx={{ ml: 2 }}>
              <Typography variant="subtitle2" color="#aaa">Business Priority</Typography>                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                <Chip 
                  label={`Priority ${submission.priority}/5`}
                  color={submission.priority > 3 ? 'error' : 'default'}
                  size="small"
                  onClick={() => handleTagClick(`priority-${submission.priority}`)}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip 
                  label={`Tedium ${submission.boredom_level}/5`}
                  color={submission.boredom_level > 3 ? 'warning' : 'default'}
                  size="small"
                  onClick={() => handleTagClick(`tedium-${submission.boredom_level}`)}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
              <Typography variant="subtitle2" color="#aaa">Bottlenecks & Pain Points</Typography>
              <Typography sx={{ mb: 2 }}>{submission.bottlenecks || <i>None</i>}</Typography>
              <Typography variant="subtitle2" color="#aaa">Expected Benefits</Typography>
              <Typography sx={{ mb: 2 }}>{submission.expected_benefits || <i>None</i>}</Typography>
            </Box>
          </Box>

          {/* Technical Requirements */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ color: '#4a9bfc', mb: 1, fontWeight: 500 }}>
              Technical Environment
            </Typography>
            <Box sx={{ ml: 2 }}>
              <Typography variant="subtitle2" color="#aaa">Current Systems</Typography>
              {Array.isArray(submission.used_systems) && submission.used_systems.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {submission.used_systems.map((system, idx) => (
                    <Chip 
                      key={idx} 
                      label={system} 
                      size="small" 
                      variant="outlined"
                      onClick={() => handleTagClick(`system-${system}`)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography sx={{ mb: 2 }}><i>None specified</i></Typography>
              )}
              
              <Typography variant="subtitle2" color="#aaa">System Types</Typography>
              {Array.isArray(submission.system_type_selection) && submission.system_type_selection.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {submission.system_type_selection.map((type, idx) => (
                    <Chip 
                      key={idx} 
                      label={type} 
                      size="small" 
                      variant="outlined"
                      onClick={() => handleTagClick(`type-${type}`)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography sx={{ mb: 2 }}><i>None specified</i></Typography>
              )}

              <Typography variant="subtitle2" color="#aaa">Platforms</Typography>
              {Array.isArray(submission.platform_selection) && submission.platform_selection.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {submission.platform_selection.map((platform, idx) => (
                    <Chip 
                      key={idx} 
                      label={platform} 
                      size="small" 
                      variant="outlined"
                      onClick={() => handleTagClick(`platform-${platform}`)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography sx={{ mb: 2 }}><i>None specified</i></Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Right Column */}
        <Box>
          {/* Data & Workflow Information */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ color: '#4a9bfc', mb: 1, fontWeight: 500 }}>
              Process Details
            </Typography>
            <Box sx={{ ml: 2 }}>
              <Typography variant="subtitle2" color="#aaa">Input Data Sources</Typography>
              <Typography sx={{ mb: 2 }}>{submission.input_data || <i>None</i>}</Typography>
              <Typography variant="subtitle2" color="#aaa">Output & Deliverables</Typography>
              <Typography sx={{ mb: 2 }}>{submission.output_data || <i>None</i>}</Typography>
            </Box>
          </Box>

          {/* Improvement Goals */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ color: '#4a9bfc', mb: 1, fontWeight: 500 }}>
              Improvement Opportunities
            </Typography>
            <Box sx={{ ml: 2 }}>
              <Typography variant="subtitle2" color="#aaa">Improvement Goals</Typography>
              {Array.isArray(submission.improvement_goals) && submission.improvement_goals.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {submission.improvement_goals.map((goal, idx) => (
                    <Chip 
                      key={idx} 
                      label={goal} 
                      size="small" 
                      color="primary"
                      onClick={() => handleTagClick(`goal-${goal}`)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography sx={{ mb: 2 }}><i>None specified</i></Typography>
              )}

              <Typography variant="subtitle2" color="#aaa">Related/Similar Tasks</Typography>
              {submission.similar_tasks && (
                <Typography sx={{ mb: 1 }}>{submission.similar_tasks}</Typography>
              )}
              {submission.similar_tasks_description && (
                <Typography sx={{ mb: 2, pl: 2, borderLeft: '2px solid #444' }}>
                  {submission.similar_tasks_description}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Attachments Section */}
      {submission.files && submission.files.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: 2 
          }}>
            <Typography variant="subtitle1" sx={{ color: '#4a9bfc', fontWeight: 500 }}>
              <AttachFileIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Attachments ({submission.files.length})
            </Typography>
          </Box>
          <Box sx={{ ml: 2 }}>
            <List>
              {submission.files.map((file, idx) => (
                <ListItem
                  key={idx}
                  sx={{
                    border: '1px solid #444',
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: 'rgba(0, 0, 0, 0.2)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'rgba(74, 155, 252, 0.1)',
                      borderColor: '#4a9bfc'
                    }
                  }}
                  secondaryAction={
                    <Tooltip title="Download File">
                      <IconButton
                        edge="end"
                        size="small"
                        sx={{ 
                          color: '#4a9bfc',
                          '&:hover': {
                            color: '#fff',
                            bgcolor: '#4a9bfc'
                          }
                        }}
                        onClick={() => {
                          // Create a download link from base64 data
                          const a = document.createElement('a');
                          a.href = file.data;
                          // Use original filename if available, otherwise generate one
                          a.download = file.name || `File-${idx}.${getExtensionFromContentType(file.type)}`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                        }}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <ListItemIcon sx={{ color: '#4a9bfc' }}>
                    {getFileIcon(file.name, file.type)}
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>
                        {file.name || `File-${idx}.${getExtensionFromContentType(file.type)}`}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" sx={{ color: '#888' }}>
                        {getFileType(file.name, file.type)} • {formatFileSize(file.size)}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      )}

      <Divider sx={{ mt: 2, mb: 1 }} />
      <Typography variant="caption" color="#888">IP: {submission.ip_address || 'N/A'} | User Agent: {submission.user_agent || 'N/A'}</Typography>
    </Paper>
  );
}

const FeedbackReader = () => {
  const [filter, setFilter] = useState('');
  const [activeTags, setActiveTags] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [prioritySort, setPrioritySort] = useState('none'); // 'none', 'asc', 'desc'
  
  const handleTagClick = (tag) => {
    if (activeTags.includes(tag)) {
      setActiveTags(activeTags.filter(t => t !== tag));
      setFilter(filter.replace(`#${tag} `, '').replace(`#${tag}`, ''));
    } else {
      setActiveTags([...activeTags, tag]);
      setFilter(`${filter}${filter.length > 0 ? ' ' : ''}#${tag}`);
    }
  };
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const { useSmallSidebar, autoHideSidebar, isSidebarOpen } = useContext(SettingsContext);
  const sidebarWidth = autoHideSidebar && !isSidebarOpen ? 0 : (useSmallSidebar ? 120 : 180);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Hent alle saker uten filtrering fra backend
      const res = await axios.get('/docs/feedback/list', {
        params: {
          limit: 1000 // Hent mange for å kunne filtrere lokalt
        },
        headers: getAuthHeaders(),
      });

      let filteredRows = res.data.submissions;

      // Anvendt filtrering basert på tags og fritekst
      if (filter) {
        const tags = filter.match(/#[\w-]+/g) || [];
        const textFilter = filter.replace(/#[\w-]+/g, '').trim();

        // Filtrer basert på tags
        if (tags.length > 0) {
          filteredRows = filteredRows.filter(row => {
            return tags.every(tag => {
              // Fjern # fra starten av taggen
              const tagWithoutHash = tag.substring(1);
              
              // Sjekk ulike tag typer
              if (tagWithoutHash.startsWith('priority-')) {
                return row.priority === parseInt(tagWithoutHash.split('-')[1]);
              }
              if (tagWithoutHash.startsWith('tedium-')) {
                return row.boredom_level === parseInt(tagWithoutHash.split('-')[1]);
              }
              if (tagWithoutHash.startsWith('system-')) {
                return row.used_systems?.includes(tagWithoutHash.split('-')[1]);
              }
              if (tagWithoutHash.startsWith('type-')) {
                return row.system_type_selection?.includes(tagWithoutHash.split('-')[1]);
              }
              if (tagWithoutHash.startsWith('platform-')) {
                return row.platform_selection?.includes(tagWithoutHash.split('-')[1]);
              }
              if (tagWithoutHash.startsWith('goal-')) {
                return row.improvement_goals?.includes(tagWithoutHash.split('-')[1]);
              }
              if (tagWithoutHash.startsWith('contact-')) {
                return row.contact_person === tagWithoutHash.split('-')[1];
              }
              if (tagWithoutHash.startsWith('email-')) {
                return row.email === tagWithoutHash.split('-')[1];
              }
              return false;
            });
          });
        }

        // Anvendt fritekst filter hvis det finnes
        if (textFilter) {
          const searchTerms = textFilter.toLowerCase().split(' ');
          filteredRows = filteredRows.filter(row => {
            const searchableText = `${row.business_area} ${row.contact_person} ${row.email} ${row.workflow_description}`.toLowerCase();
            return searchTerms.every(term => searchableText.includes(term));
          });
        }
      }

      // Sorter etter prioritet hvis aktivert
      if (prioritySort !== 'none') {
        filteredRows.sort((a, b) => {
          const priorityA = a.priority || 0;
          const priorityB = b.priority || 0;
          return prioritySort === 'asc' ? priorityA - priorityB : priorityB - priorityA;
        });
      }

      // Paginer resultatet
      const start = (page - 1) * pageSize;
      const paginatedRows = filteredRows.slice(start, start + pageSize);
      
      setRows(paginatedRows.map(row => ({ ...row, id: row.id })));
      setRowCount(filteredRows.length);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filter, prioritySort]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    if (selectedId == null) return setSelectedDetail(null);
    setSelectedDetail(null);
    setLoading(true);
    axios.get(`/docs/feedback/get/${selectedId}`, {
      headers: getAuthHeaders(),
    })
      .then((res) => setSelectedDetail(res.data.submission))
      .catch((e) => setError(e.response?.data?.error || e.message || 'Failed to load detail'))
      .finally(() => setLoading(false));
  }, [selectedId]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: sidebarWidth,
        width: `calc(100vw - ${sidebarWidth}px)`,
        height: '100vh',
        paddingBottom: `${STATUS_BAR_HEIGHT}px`,
        boxSizing: 'border-box',
        background: '#1e1e1e',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
        <TabView
          tabLabels={["Automation Assessment"]}
          tabContents={[
            <Box key="main" sx={{
              p: 0,
              color: '#fff',
              minHeight: 0,
              height: '100%',
              width: '100%',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <SplitView
                showFilter
                filterProps={{
                  label: "Filter / #tags #priority-5",
                  value: filter,
                  onChange: e => { setFilter(e.target.value); fetchRows(); },
                  InputProps: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={`Sort by priority: ${prioritySort === 'none' ? 'Off' : prioritySort === 'asc' ? 'Low to High' : 'High to Low'}`}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              const nextSort = prioritySort === 'none' ? 'desc' : prioritySort === 'desc' ? 'asc' : 'none';
                              setPrioritySort(nextSort);
                              fetchRows();
                            }}
                            sx={{ 
                              color: prioritySort === 'none' ? 'action.disabled' : 'primary.main',
                              transform: prioritySort === 'asc' ? 'rotate(180deg)' : 'none'
                            }}
                          >
                            <SortIcon />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    )
                  }
                }}
                listPanel={
                  <List dense disablePadding sx={{ width: '100%', flex: 1 }}>
                    {loading && (
                      <ListItem><CircularProgress size={20} sx={{ mx: 'auto' }} /></ListItem>
                    )}
                    {error && (
                      <ListItem><Alert severity="error">{error}</Alert></ListItem>
                    )}
                    {!loading && !error && rows.length === 0 && (
                      <ListItem><Typography sx={{ color: '#aaa' }}>No feedback submissions found.</Typography></ListItem>
                    )}
                    {rows.map((row) => (
                      <ListItem
                        key={row.id}
                        button
                        selected={row.id === selectedId}
                        onClick={() => setSelectedId(row.id)}
                        sx={{
                          bgcolor: row.id === selectedId ? '#232323' : 'inherit',
                          borderLeft: row.id === selectedId ? '3px solid #ff6600' : '3px solid transparent',
                          py: 0.5, px: 1.5, minHeight: 44,
                          '&:hover': { bgcolor: '#232323' },
                        }}
                      >
                        <ListItemText
                          primary={<>
                            <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>
                              {row.workflow_description ? row.workflow_description.slice(0, 60) + (row.workflow_description.length > 60 ? '...' : '') : 'No description'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#bbb' }}>
                              {row.contact_person || 'No contact'} @ {row.business_area || 'No area'}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', color: '#888' }}>
                              {formatDateTime(row.timestamp)}
                            </Typography>
                          </>}
                        />
                      </ListItem>
                    ))}
                  </List>
                }
                detailPanel={
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {loading && <Box sx={{ textAlign: 'center', mt: 2 }}><CircularProgress /></Box>}
                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                    {!loading && !error && rows.length === 0 && (
                      <Typography sx={{ color: '#aaa', mt: 2 }}>No feedback submissions found.</Typography>
                    )}
                    {selectedDetail && <FeedbackDetail submission={selectedDetail} handleTagClick={handleTagClick} />}
                  </Box>
                }
              />
            </Box>
          ]}
          variant="small"
          initialActiveTab="Automation Assessment"
          disableUrlUpdate={true}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            minWidth: 0,
            p: 0,
            m: 0,
            boxSizing: 'border-box',
            overflow: 'hidden',
            '& .tab-content-container': {
              flex: 1,
              overflow: 'hidden',
            },
          }}
        />
      </div>
    </div>
  );
};

export default FeedbackReader;
